<template>
  <v-container
    fluid
    style="height: 100%;"
    class="pa-4"
  >
    <v-row
      class="mb-6"
      justify="center"
    >
      <v-col
        cols="12"
        md="10"
      >
        <base-material-card
          icon="mdi-shield-account"
          class="pa-3 text-end"
          color="secondary"
          :loading="loading"
        >
          <v-sheet
            class="pa-6 pb-0 rounded-lg"
            elevation="0"
          >
            <v-col
              class="d-flex justify-end"
              cols="12"
              md="12"
              sm="6"
            >
              <v-btn
                color="primary"
                dark
                large
                class="rounded-pill"
                @click="openNewUserDialog = true"
              >
                <v-icon left>
                  mdi-plus-circle
                </v-icon>
                Novo Usuário
              </v-btn>
            </v-col>
            <v-divider class="my-4" />
            <v-form
              v-model="valid"
              class="pe-3"
              @submit.prevent="searchResult()"
            >
              <v-row
                align="center"
                class="pb-2 ps-3"
              >
                <v-radio-group
                  v-model="searchBy"
                  row
                  label="Buscar por:"
                  hide-details
                >
                  <v-radio
                    label="Nome"
                    value="name"
                    color="primary"
                  />
                  <v-radio
                    label="Email"
                    value="username"
                    color="primary"
                  />
                </v-radio-group>
              </v-row>
              <v-row align="center">
                <v-col
                  class="ms-0 pe-0 pt-6 d-flex align-center"
                  cols="12"
                  md="9"
                  sm="6"
                >
                  <v-text-field
                    v-model="searchValue"
                    class="rounded-lg"
                    :label="searchBy === 'name' ? 'Digite um nome para buscar...' : 'Digite um email para buscar...'"
                    :placeholder="searchBy === 'name' ? 'Ex: João Silva' : 'Ex: joao.silva@email.com'"
                    counter="60"
                    :rules="searchBy === 'name' ? [rules.noCoding] : [rules.email]"
                    clearable
                    outlined
                    dense
                    prepend-inner-icon="mdi-magnify"
                    hide-details="auto"
                  />
                </v-col>
                <v-col
                  class="pt-0 pe-0 ps-0 d-flex align-center justify-end"
                  cols="12"
                  md="3"
                  sm="6"
                >
                  <v-btn
                    color="info"
                    class="rounded-pill px-6 mt-n1"
                    large
                    type="submit"
                    :loading="isLoadingSearch"
                    :disabled="!searchValue || isLoadingSearch"
                  >
                    <v-icon left>
                      mdi-account-search
                    </v-icon>
                    Buscar Usuário
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-sheet>
          <v-sheet
            v-if="getUsers.length || searchPerformed"
            class="mt-6 ps-8 pe-7 rounded-lg"
            elevation="0"
          >
            <h3 class="text-start text-h4 mb-4 font-weight-medium">
              Resultados da Busca:
            </h3>
            <v-divider class="mb-4" />
            <div v-if="pagedUsers.length">
              <v-card
                v-for="(person, index) in pagedUsers"
                :key="index"
                class="mb-3 rounded-lg elevation-2"
              >
                <v-row
                  align="center"
                  class="pt-0"
                >
                  <v-col class="d-flex align-center">
                    <v-icon
                      left
                      color="primary"
                      class="mr-3"
                    >
                      mdi-account-circle
                    </v-icon>
                    <div class="text-start">
                      <span class="text-h4 font-weight-medium">{{ person.name }}</span>
                      <br>
                      <span class="text-subtitle-2 grey--text">{{ person.username }}</span>
                    </div>
                  </v-col>
                  <v-spacer />
                  <v-col cols="auto">
                    <v-btn
                      color="secondary"
                      outlined
                      class="rounded-pill"
                      @click.stop="openDetails(person._id)"
                    >
                      <v-icon left>
                        mdi-information-outline
                      </v-icon>
                      Detalhes
                    </v-btn>
                  </v-col>
                </v-row>
                <v-expand-transition>
                  <div v-if="selectedUser === person._id">
                    <v-divider class="my-2" />
                    <user-details
                      v-model="selectedUser"
                      persistent
                      @reload="searchResult()"
                    />
                  </div>
                </v-expand-transition>
              </v-card>
              <!-- Pagination controls -->
              <v-pagination
                v-if="totalPages > 1"
                v-model="currentPage"
                :length="totalPages"
                total-visible="7"
                class="mt-5 pb-4"
                color="primary"
              />
            </div>
            <v-alert
              v-else-if="searchPerformed && !pagedUsers.length && !isLoadingSearch"
              type="info"
              prominent
              icon="mdi-information"
              class="rounded-lg text-start text-h5"
            >
              Nenhum usuário encontrado com os critérios informados. Por favor, tente novamente.
            </v-alert>
          </v-sheet>
          <!-- Loading spinner para a busca geral -->
          <div
            v-if="isLoadingSearch"
            class="text-center mt-8"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              width="6"
            />
            <p class="mt-4 text-subtitle-1 grey--text">
              Buscando usuários...
            </p>
          </div>
        </base-material-card>
      </v-col>
    </v-row>
    <v-dialog
      v-model="openNewUserDialog"
      max-width="550px"
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card
        elevation="12"
        class="rounded-lg"
      >
        <v-card-title class="text-h5 primary--text d-flex justify-space-between align-center py-4 px-6">
          <span class="text-h5 font-weight-bold">Criar Novo Usuário</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="py-6 px-6">
          <v-form v-model="newUserValid">
            <v-text-field
              v-model="newUser.name"
              label="Nome Completo"
              :rules="[rules.required, rules.noCoding]"
              counter="60"
              required
              outlined
              dense
              class="mb-4 rounded-lg"
              prepend-inner-icon="mdi-account"
            />
            <v-text-field
              v-model="newUser.username"
              label="Email (Nome de Usuário)"
              :rules="[rules.required, rules.email, rules.noCoding]"
              required
              counter="60"
              outlined
              dense
              class="mb-4 rounded-lg"
              prepend-inner-icon="mdi-email"
            />
            <v-text-field
              v-model="newUser.password"
              label="Senha"
              :type="showPassword ? 'text' : 'password'"
              :rules="[rules.required, rules.password]"
              required
              outlined
              dense
              class="rounded-lg"
              prepend-inner-icon="mdi-lock"
            >
              <template v-slot:append>
                <v-icon
                  class="mr-2"
                  title="Mostrar/Ocultar Senha"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? 'mdi-eye' : 'mdi-eye-off' }}
                </v-icon>
                <v-icon
                  :loading="isLoadingPassword"
                  title="Gerar Senha Aleatória"
                  @click="createRandomPassword"
                >
                  mdi-autorenew
                </v-icon>
              </template>
            </v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 d-flex justify-end">
          <v-btn
            text
            color="error"
            class="rounded-pill"
            @click="openNewUserDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!newUserValid || isLoadingCreate"
            :loading="isLoadingCreate"
            class="rounded-pill px-6"
            @click="createNewUser"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>
            Salvar Usuário
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import rules from '../../services/rules'
  import UserDetails from './components/users/UserDetails.vue'

  export default {
    name: 'ListUsers',
    components: {
      UserDetails,
    },
    data () {
      return {
        valid: false,
        searchBy: 'name',
        searchValue: '',
        rules: rules,
        selectedUser: null,
        openNewUserDialog: false,
        newUserValid: false,
        newUser: {
          name: '',
          username: '',
          password: '',
          scope: 'user',
        },
        showPassword: false,
        isLoadingSearch: false,
        isLoadingCreate: false,
        isLoadingPassword: false,
        searchPerformed: false,
        loading: false,
        // Pagination data properties
        currentPage: 1,
        itemsPerPage: 10, // Max 10 users per page
      }
    },
    computed: {
      ...mapGetters('users', ['getUsers']),
      ...mapGetters('customers', ['customerList']),
      // Computed property for paginated users
      pagedUsers () {
        const start = (this.currentPage - 1) * this.itemsPerPage
        const end = start + this.itemsPerPage
        return this.getUsers.slice(start, end)
      },
      // Computed property for total number of pages
      totalPages () {
        return Math.ceil(this.getUsers.length / this.itemsPerPage)
      },
    },
    watch: {
      openNewUserDialog (newVal) {
        if (!newVal) {
          this.newUser = { name: '', username: '', password: '', scope: 'user' }
          this.newUserValid = false
          this.showPassword = false
        }
      },
      searchBy (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.searchValue = ''
          this.searchPerformed = false
          this.currentPage = 1 // Reset page when search criteria changes
        }
      },
      // Watch for changes in getUsers to reset current page if needed
      getUsers () {
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages
        } else if (this.totalPages === 0) {
          this.currentPage = 1
        }
      },
    },
    methods: {
      ...mapActions('users', ['findUser', 'changePassword', 'deleteUser', 'createUser', 'generateRandomPassword']),
      ...mapActions('customers', ['listCustomers']),
      async searchResult () {
        this.isLoadingSearch = true
        this.searchPerformed = true
        this.currentPage = 1 // Reset to the first page on a new search

        if (!this.searchValue) {
          // Assuming $alert is a global mixin or similar
          this.$alert('error', 'Por favor, preencha o campo de busca.')
          this.isLoadingSearch = false
          return
        }
        const searchPayload = this.searchBy === 'name' ? { name: this.searchValue } : { username: this.searchValue }
        try {
          await this.findUser(searchPayload)
          // No need to check getUsers.length here, pagedUsers will handle it
        } catch (error) {
          console.error('Erro ao buscar usuário. Verifique os dados e tente novamente.', error)
          // Assuming $alert is available
          this.$alert('error', 'Erro ao buscar usuário. Verifique os dados e tente novamente.')
        } finally {
          this.isLoadingSearch = false
        }
      },
      async createNewUser () {
        if (this.newUserValid) {
          this.isLoadingCreate = true
          try {
            await this.createUser(this.newUser)
            // Assuming $alert is available
            this.$alert('success', 'Usuário criado com sucesso!')
            this.openNewUserDialog = false
            this.newUser = { name: '', username: '', password: '', scope: 'user' }

            if (this.searchPerformed && this.searchValue) {
              // Re-run search to update the list with the new user, if applicable
              this.searchResult()
            }
          } catch (error) {
            console.error('Erro ao criar usuário:', error)
            // Assuming $alert is available
            this.$alert('error', `Erro ao criar usuário: ${error.message || 'Erro desconhecido'}`)
          } finally {
            this.isLoadingCreate = false
          }
        }
      },
      async createRandomPassword () {
        this.isLoadingPassword = true
        try {
          this.newUser.password = await this.generateRandomPassword()
        } catch (error) {
          console.error('Erro ao gerar senha aleatória:', error)
          // Assuming $alert is available
          this.$alert('error', 'Erro ao gerar senha aleatória.')
        } finally {
          this.isLoadingPassword = false
        }
      },
      openDetails (id) {
        this.selectedUser = this.selectedUser === id ? null : id
      },
    },
  }
</script>

<style scoped>
.v-card {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
}
.v-btn.rounded-pill {
  border-radius: 50px !important;
  text-transform: none !important;
  font-weight: 600;
}
.v-text-field.rounded-lg .v-input__control {
  border-radius: 8px !important;
}

.v-card-title .v-btn.v-btn--icon {
  margin-right: -12px;
}

.v-pagination {
  justify-content: center;
}
</style>
